import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Icon,
  Input,
  Modal,
  SearchBox,
  StatusTip,
  TagSelect,
} from 'tea-component';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { useDblIntl } from '@/pages/datalab/utils/utils';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import './index.scss';
import {
  DimensionIconCategory,
  SHOW_DIMENSION_ICON,
  SHOW_DIMENSION_ICON_MAP,
  SHOW_DIMENSION_ICON_CATEGORY_KEYS,
  normalizeDimensionIconKey,
  isDimensionTrendIconIconfont,
  type IDimensionTrendIconMeta,
  type TDimensionIconCategory,
} from '@dbl/core/chartEditor';
import type { IColumnInfo } from '@dbl/core/dataSource';
import SvgIcon from '@home/components/SvgIcon';
import { formatCountryDimensionIconName } from '@/pages/datalab/utils/dimensionCountryIconLabel';
import {
  DIMENSION_ICON_KEY_TO_CATEGORY,
  filterPickableDimensionIconsByQuery,
  sortDimensionIconKeysForDisplay,
} from '@/pages/datalab/utils/dimensionIconPickerSearch';

interface OptionItemType {
  name: string;
  icon: string;
  values: string[];
  key: number;
}

interface DimensionMapSetType {
  modalInfo: { isVisible: boolean; currentDrag: IColumnInfo[]; };
  options: OptionItemType[];
  onClose: () => void;
  dispatchFun: () => void;
  dimensionMap: {};
}

function renderDimensionIconThumb(
  meta: IDimensionTrendIconMeta,
  className: string,
): React.ReactNode {
  if (isDimensionTrendIconIconfont(meta)) {
    return (
      <SvgIcon
        type={meta.iconfontType}
        className={classNames(className, `${className}--iconfont`)}
      />
    );
  }
  return <img className={className} src={meta.icon} alt="" />;
}

function getInitialIconCategory(
  selectedCanon: string,
): TDimensionIconCategory {
  if (!selectedCanon) return DimensionIconCategory.Platform;
  const canon = normalizeDimensionIconKey(selectedCanon);
  for (const cat of SHOW_DIMENSION_ICON_CATEGORY_KEYS) {
    const list = SHOW_DIMENSION_ICON[cat].map(normalizeDimensionIconKey);
    if (list.includes(canon)) return cat;
  }
  return DimensionIconCategory.Platform;
}

interface IDimensionIconPickerPanelProps {
  selectedCanon: string;
  onPick: (canonicalKey: string) => void;
  dragFormatMessage: (id: string) => string;
}

const DimensionIconPickerPanel: React.FC<IDimensionIconPickerPanelProps> = ({
  selectedCanon,
  onPick,
  dragFormatMessage,
}) => {
  const { locale } = useIntl();
  const [category, setCategory] = useState<TDimensionIconCategory>(() =>
    getInitialIconCategory(selectedCanon),
  );
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    setCategory(getInitialIconCategory(selectedCanon));
  }, [selectedCanon]);

  useEffect(() => {
    setSearchQuery('');
  }, [selectedCanon]);

  const categoryKeys = useMemo(() => {
    return SHOW_DIMENSION_ICON[category]
      .map(normalizeDimensionIconKey)
      .filter((k) => SHOW_DIMENSION_ICON_MAP[k]);
  }, [category]);

  const trimmedSearch = searchQuery.trim();
  const isSearchMode = Boolean(trimmedSearch);

  const labelForSort = useCallback(
    (k: string) => {
      const cat = DIMENSION_ICON_KEY_TO_CATEGORY.get(k);
      if (!cat) {
        return k;
      }
      return cat === DimensionIconCategory.Country
        ? formatCountryDimensionIconName(k, locale)
        : dragFormatMessage(`dimension_icon_name_${k}`);
    },
    [locale, dragFormatMessage],
  );

  const globalSearchResultKeys = useMemo(() => {
    if (!trimmedSearch) {
      return [];
    }
    const matched = filterPickableDimensionIconsByQuery(trimmedSearch);
    return sortDimensionIconKeysForDisplay(matched, locale, labelForSort);
  }, [trimmedSearch, locale, labelForSort]);

  const handlePick = (canonicalKey: string): void => {
    onPick(canonicalKey);
  };

  const activeCategoryTitle = dragFormatMessage(
    `dimension_icon_category_${category}`,
  );

  const renderOptionRow = (
    key: string,
    optionCategory: TDimensionIconCategory,
    showCategoryBadge: boolean,
  ): React.ReactNode => {
    const meta = SHOW_DIMENSION_ICON_MAP[key];
    const isSelected = selectedCanon === key;
    return (
      <div
        key={key}
        role="button"
        tabIndex={0}
        className={classNames('dbl-icon-picker-panel__option', {
          'dbl-icon-picker-panel__option--selected': isSelected,
        })}
        onClick={() => handlePick(key)}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handlePick(key);
          }
        }}
      >
        {renderDimensionIconThumb(
          meta,
          'dbl-icon-picker-panel__option-icon',
        )}
        {showCategoryBadge ? (
          <span className="dbl-icon-picker-panel__option-category">
            {dragFormatMessage(`dimension_icon_category_${optionCategory}`)}
          </span>
        ) : null}
        <span className="dbl-icon-picker-panel__option-label">
          {optionCategory === DimensionIconCategory.Country
            ? formatCountryDimensionIconName(key, locale)
            : dragFormatMessage(`dimension_icon_name_${key}`)}
        </span>
        <span
          className={classNames(
            'dbl-icon-picker-panel__option-check-wrap',
            !isSelected &&
              'dbl-icon-picker-panel__option-check-wrap--empty',
          )}
          aria-hidden
        >
          {isSelected ? (
            <svg
              className="dbl-icon-picker-panel__option-check"
              viewBox="0 0 16 16"
              width="14"
              height="14"
              focusable="false"
            >
              <path
                d="M3.5 8.2 L7 11.7 L12.5 4.3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : null}
        </span>
      </div>
    );
  };

  return (
    <div
      className="dbl-icon-picker-panel"
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <div className="dbl-icon-picker-panel__search">
        <SearchBox
          value={searchQuery}
          onChange={(v: string) => setSearchQuery(v)}
          placeholder={dragFormatMessage(
            'dimension_icon_search_placeholder',
          )}
        />
      </div>
      {isSearchMode ? (
        <div className="dbl-icon-picker-panel__body dbl-icon-picker-panel__body--search">
          <div
            className="dbl-icon-picker-panel__search-results"
            role="tabpanel"
          >
            {globalSearchResultKeys.length === 0 ? (
              <div className="dbl-icon-picker-panel__search-empty">
                {dragFormatMessage('dimension_icon_search_no_results')}
              </div>
            ) : (
              globalSearchResultKeys.map((key) =>
                renderOptionRow(
                  key,
                  DIMENSION_ICON_KEY_TO_CATEGORY.get(key)!,
                  true,
                ),
              )
            )}
          </div>
        </div>
      ) : (
        <div className="dbl-icon-picker-panel__body">
          <div className="dbl-icon-picker-panel__left">
            <div className="dbl-icon-picker-panel__column-head">
              {dragFormatMessage('dimension_icon_column_category')}
            </div>
            <div className="dbl-icon-picker-panel__categories" role="tablist">
              {SHOW_DIMENSION_ICON_CATEGORY_KEYS.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  role="tab"
                  aria-selected={category === cat}
                  className={classNames('dbl-icon-picker-panel__category', {
                    'dbl-icon-picker-panel__category--active':
                      category === cat,
                  })}
                  onClick={() => setCategory(cat)}
                >
                  <span className="dbl-icon-picker-panel__category-text">
                    {dragFormatMessage(`dimension_icon_category_${cat}`)}
                  </span>
                  <Icon
                    type="arrowright"
                    className="dbl-icon-picker-panel__category-arrow"
                  />
                </button>
              ))}
            </div>
          </div>
          <div className="dbl-icon-picker-panel__right">
            <div className="dbl-icon-picker-panel__column-head">
              {activeCategoryTitle}
            </div>
            <div className="dbl-icon-picker-panel__options" role="tabpanel">
              {categoryKeys.map((key) =>
                renderOptionRow(key, category, false),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function DimensionMapSetModal(props: DimensionMapSetType) {
  const { modalInfo, onClose, dispatchFun, dimensionMap } = props;
  const { dblFormatMessage: dragFormatMessage, formatMessage } =
    useDblIntl('sortable_drag');
  const formData = useRef([
    { name: '', icon: '', values: [] },
    { name: '', icon: '', values: [] },
    { name: '', icon: '', values: [] },
    { name: '', icon: '', values: [] },
    { name: '', icon: '', values: [] },
  ]);
  const [formValue, setFormValue] = useState<any>({
    maplist: formData.current,
  });
  const [options, setOptions] = useState<any>({
    data: [],
    status: true,
  });

  useEffect(() => {
    const newData = formData.current.map(
      (item, index) => dimensionMap?.data?.[index] || item,
    );
    setFormValue({ maplist: newData });
    setOptions({ data: dimensionMap?.options || [], status: dimensionMap.loading });
  }, [JSON.stringify(dimensionMap)]);

  useEffect(() => {
    dispatchFun('getDimensionOption', { currentDrag: modalInfo.currentDrag });
  }, []);

  const validationSchema = Yup.object().shape({
    maplist: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .trim()
          .test(
            'ok',
            dragFormatMessage('dimension_map_require'),
            function (value) {
              const { values } = this.parent;
              if (
                values &&
                values.length > 0 &&
                (!value || value.trim().length === 0)
              ) {
                return false;
              }
              return true;
            },
          )
          .test(
            'ok',
            dragFormatMessage('dimension_map_max'),
            (val: any) => (val?.trim() ? val?.length <= 20 : true),
          )
          .test(
            'ok',
            dragFormatMessage('dimension_map_name_duplicate'),
            (val: any) =>
              formik.values?.maplist?.filter(
                (item: any) => item.name === val && item.name,
              )?.length <= 1,
          ),
        values: Yup.array().test(
          'ok',
          dragFormatMessage('dimension_map_require'),
          function (array) {
            const { name } = this.parent;
            if (
              name &&
              name.trim().length > 0 &&
              (!array || array.length === 0)
            ) {
              return false;
            }
            return true;
          },
        ),
      }),
    ),
  });

  const formik = useFormik({
    initialValues: formValue,
    enableReinitialize: true,
    validationSchema,
    validate: () => {},
    onSubmit: async () => true,
  });

  const buttonStatus = useMemo(
    () =>
      formik.values?.maplist.some(
        (item: OptionItemType) => item.name.trim() && item.values.length > 0,
      ),
    [formik.values?.maplist],
  );

  const saveData = async () => {
    const isValid = await formik.submitForm();
    if (isValid && buttonStatus) {
      const submitData = formik.values?.maplist?.filter(
        (item: OptionItemType) => item.name && item.values.length !== 0,
      );
      dispatchFun('save', {
        customDimensionMap: { data: submitData, options: dimensionMap.options },
      });
      dispatchFun('saveDrag', {
        dragState: {
          currentDrag: '',
          errorDrag: {},
        },
      });
      onClose();
    }
  };

  const renderIconPicker = (index: number) => {
    const rowIcon = formik.values.maplist[index]?.icon as string;
    const selectedCanon = normalizeDimensionIconKey(rowIcon);
    return (
      <DimensionIconPickerPanel
        selectedCanon={selectedCanon}
        dragFormatMessage={dragFormatMessage}
        onPick={(canonicalKey: string) => {
          const maplist = [...formik.values.maplist] as any;
          maplist[index].icon = canonicalKey;
          formik.setValues({ maplist });
        }}
      />
    );
  };

  return (
    <Modal
      visible={modalInfo.isVisible}
      size={836}
      className={'dbl-dimension-map-set-modal'}
      caption={dragFormatMessage('dimension_map_set')}
      onClose={() => onClose()}
    >
      <Modal.Body>
        {formik.values?.maplist?.map((item: any, index: number) => {
          const iconCanon = item.icon
            ? normalizeDimensionIconKey(item.icon)
            : '';
          const iconMeta =
            iconCanon && SHOW_DIMENSION_ICON_MAP[iconCanon]
              ? SHOW_DIMENSION_ICON_MAP[iconCanon]
              : SHOW_DIMENSION_ICON_MAP.default;
          return (
            <Form
              className={'dbl-dimension-map-setting-form'}
              layout="inline"
              key={item.index}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
            >
              <Form.Item
                className={'dbl-dimension-map-setting-form-item'}
                status={
                  formik.errors.maplist?.[index]?.name &&
                  formik.touched.maplist?.[index]?.name
                    ? 'error'
                    : undefined
                }
                message={
                  formik.errors.maplist?.[index]?.name &&
                  formik.touched.maplist?.[index]?.name
                    ? formik.errors.maplist?.[index]?.name
                    : undefined
                }
              >
                <Input
                  {...formik.getFieldProps(`maplist[${index}].name`)}
                  className="dbl-dimension-map-setting-form-item-name"
                  placeholder={dragFormatMessage('dimension_map_name')}
                  onBlur={formik.handleBlur}
                  onChange={(value) => {
                    const maplist = [...formik.values.maplist] as any;
                    maplist[index].name = value;
                    formik.setValues({ maplist });
                  }}
                />
              </Form.Item>
              <span className={'dbl-dimension-map-setting-split'}>-</span>
              <Form.Item
                className={'dbl-dimension-map-setting-form-item form-dropdown'}
                status={
                  formik.errors.maplist?.[index]?.icon &&
                  formik.touched.maplist?.[index]?.icon
                    ? 'error'
                    : undefined
                }
                message={
                  formik.errors.maplist?.[index]?.icon &&
                  formik.touched.maplist?.[index]?.icon
                    ? formik.errors.maplist?.[index]?.icon
                    : undefined
                }
              >
                <Dropdown
                  className="dbl-dimension-map-setting-form-item-icon"
                  overlayClassName="dbl-map-setting-icon-dropdown"
                  appearance="button"
                  clickClose={false}
                  button={
                    item.icon ? (
                      renderDimensionIconThumb(
                        iconMeta,
                        'dbl-dimension-map-setting-country-icon',
                      )
                    ) : (
                      <div className="tea-dropdown__value">
                        <span className="tea-text-weak">
                          {dragFormatMessage('dimension_map_icon')}
                        </span>
                      </div>
                    )
                  }
                >
                  {() => renderIconPicker(index)}
                </Dropdown>
              </Form.Item>
              <span className={'dbl-dimension-map-setting-split'}>=</span>
              <Form.Item
                className={'dbl-dimension-map-setting-form-item'}
                status={
                  formik.errors.maplist?.[index]?.values &&
                  formik.touched.maplist?.[index]?.values
                    ? 'error'
                    : undefined
                }
                message={
                  formik.errors.maplist?.[index]?.values &&
                  formik.touched.maplist?.[index]?.values
                    ? formik.errors.maplist?.[index]?.values
                    : undefined
                }
              >
                <TagSelect
                  {...formik.getFieldProps(`maplist[${index}].values`)}
                  className="dbl-dimension-map-setting-form-item-values"
                  options={options.data}
                  placeholder={dragFormatMessage('dimension_map_value')}
                  onChange={(value) => {
                    const maplist = [...formik.values.maplist] as any;
                    maplist[index].values = value;
                    formik.setValues({ maplist });
                  }}
                  tips={
                    options.status && (
                      <StatusTip
                        status={'loading'}
                        loadingText={formatMessage({
                          id: 'dbl_sortable_drag_loading',
                        })}
                        emptyText={formatMessage({
                          id: 'dbl_common_no_data2',
                        })}
                      />
                    )
                  }
                />
              </Form.Item>
            </Form>
          );
        })}
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={!buttonStatus}
          onClick={() => {
            saveData();
          }}
        >
          {formatMessage({ id: 'menu_confirm' })}
        </Button>
        <Button type="weak" onClick={() => onClose()}>
          {formatMessage({ id: 'menu_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
